import { useAtom, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  isViewOnlyModeAtom,
  streamingProgressAtom,
} from "@zapier/canvas-state";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import type { Message } from "@ai-sdk/ui-utils";

import { emitAiEventsSetter } from "../atoms/emitAiEventsSetter";
import { outlineAtom } from "../atoms/outlineAtom";
import { aiPromptAtom } from "../atoms/aiPromptAtom";
import { useSyncZslStreamToOutline } from "./useSyncZslStreamToOutline";
import {
  promptToZslErrorAtom,
  isPromptToZslCompleteAtom,
  isStreamingZslAtom,
} from "../atoms/streamingStatusAtoms";
import { tuningPromptsAtom } from "../atoms/tuningPromptsAtom";

export function usePromptToZslToCanvas() {
  const renderZslToCanvas = useSetAtom(emitAiEventsSetter);
  const setStringOutline = useSetAtom(outlineAtom);
  const [prompt, setPrompt] = useAtom(aiPromptAtom);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const setError = useSetAtom(promptToZslErrorAtom);
  const { setMessages } = useCopilotChat();
  const setIsViewOnlyMode = useSetAtom(isViewOnlyModeAtom);
  const setIsStreamingZsl = useSetAtom(isStreamingZslAtom);
  const setStreamingProgress = useSetAtom(streamingProgressAtom);
  const setIsComplete = useSetAtom(isPromptToZslCompleteAtom);
  const { handleSubmit } = useSyncZslStreamToOutline({
    api: "/api/zsl/v2/combined_stream",
    async onMessage() {},
    async onComplete(zsl) {
      setIsStreamingZsl(false);
      setStreamingProgress(0);
      setIsViewOnlyMode(false);

      // Update the last tool invocation with the zsl result
      setMessages((prevMessages: Message[]) => {
        return prevMessages.map((message: Message) => {
          const hasUpdateCall = message?.parts?.some(
            (invocation: any) =>
              invocation.toolInvocation?.toolName === "create_system" &&
              invocation.toolInvocation?.state === "call",
          );

          if (hasUpdateCall) {
            const completedToolInvocation = {
              ...message,
              parts: message.parts?.map((part: any) => {
                if (
                  part.type === "tool-invocation" &&
                  part.toolInvocation?.toolName === "create_system" &&
                  part.toolInvocation?.state === "call"
                ) {
                  return {
                    ...part,
                    toolInvocation: {
                      ...part.toolInvocation,
                      state: "result",
                      args: {
                        prompt,
                      },
                      result: {
                        content: [
                          {
                            text: `Canvas updated`,
                            zsl,
                          },
                        ],
                      },
                    },
                  };
                }
                return part;
              }),
            };
            return completedToolInvocation;
          }
          return message;
        });
      });

      await renderZslToCanvas();
      setIsComplete(true);

      const message = `We've created a preview of your assets. You can continue to make changes using Copilot. When you're ready, click 'Build it' to add them to your account.`;

      setMessages((prevMessages: Message[]) => {
        // Add "Build it" button after successful system creation
        const buildItMessage: Message = {
          role: "assistant",
          content: message,
          id: "build-it-button-" + Date.now(),
          createdAt: new Date(),
          parts: [
            {
              type: "text",
              text: message,
            },
            {
              type: "tool-invocation",
              toolInvocation: {
                state: "result",
                toolName: "build_it_button",
                toolCallId: "build-it-auto-" + Date.now(),
                args: {},
                result: {
                  success: true,
                  message: 'Showing "Build it" button for ZSL execution',
                },
              },
            },
          ],
        };
        return [...prevMessages, buildItMessage];
      });
    },
    onError(error: string) {
      setError(error);
    },
  });

  const onPromptSubmit = useCallback(
    async (localPrompt: string) => {
      setIsComplete(false);
      setStringOutline(""); // When user modify the prompt

      setPrompt(localPrompt);
      setTuningPrompts([]); // Reset tuning prompts for each new AI suggestion
      await handleSubmit(localPrompt);
    },
    [
      handleSubmit,
      setIsComplete,
      setPrompt,
      setStringOutline,
      setTuningPrompts,
    ],
  );

  return {
    onPromptSubmit,
  };
}
